import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, Undo2 } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { CampoDelPanel, PanelLateral, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { notificar } from '@/shared/ui/avisos';

interface NotaDelCobroAnulado {
  facturaId: number;
  numeroFactura: string | null;
  numeroNota: string | null;
  situacion: string;
  mensaje: string | null;
}

interface CobroQueSeAnula {
  id: number;
  numeroComprobante: string | null;
  cliente: string;
  total: number;
  cuantasFacturas: number;
}

export function PanelDeAnulacion({
  cobro,
  abierto,
  alCerrar,
  alAnular,
}: {
  cobro: CobroQueSeAnula;
  abierto: boolean;
  alCerrar: () => void;
  alAnular: () => void;
}) {
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const anular = useMutation({
    mutationFn: () =>
      api.post<{ notas: NotaDelCobroAnulado[] }>(`/cobros/${cobro.id}/anular`, {
        motivo: motivo.trim(),
      }),
    onSuccess: (respuesta) => {
      notificar.exito(`Cobro ${cobro.numeroComprobante ?? cobro.id} anulado`);

      for (const nota of respuesta?.notas ?? []) {
        if (nota.situacion === 'autorizada') {
          notificar.exito(
            `NOTA DE CRÉDITO ${nota.numeroNota} AUTORIZADA: LA FACTURA ${nota.numeroFactura} QUEDA SIN EFECTO.`,
            'Nota de crédito',
          );
        } else {
          notificar.advertencia(
            `LA FACTURA ${nota.numeroFactura ?? ''} NECESITA SU NOTA DE CRÉDITO: ${nota.mensaje ?? nota.situacion.toUpperCase()}. TERMÍNELA EN FACTURACIÓN › NOTAS DE CRÉDITO.`,
            'Falta la nota de crédito',
          );
        }
      }
      alAnular();
      alCerrar();
    },
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo anular el cobro';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const listo = motivo.trim().length >= 10 && !anular.isPending;

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo="Anular el cobro"
      descripcion={cobro.numeroComprobante ?? `Cobro ${cobro.id}`}
      icono={Undo2}
      alEnviar={(evento) => {
        evento.preventDefault();
        if (listo) {
          setError(null);
          anular.mutate();
        }
      }}
      enviando={!listo || anular.isPending}
    >
      <div className="rounded-xl border border-armazon-200 p-4">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-armazon-500">
          Se va a deshacer
        </p>
        <p className="importe mt-1 text-2xl font-bold text-armazon-900">
          ${cobro.total.toFixed(2)}
        </p>
        <p className="mt-0.5 text-[11px] uppercase text-armazon-500">
          {cobro.cliente} · {cobro.cuantasFacturas}{' '}
          {cobro.cuantasFacturas === 1 ? 'factura' : 'facturas'}
        </p>
      </div>

      <p className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-justify text-[11px] uppercase leading-relaxed text-amber-900">
        <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        <span>
          Lo cobrado vuelve a deberse y el total de su caja baja ${cobro.total.toFixed(2)}. El cobro
          no se borra: queda anulado, con su nombre y su motivo. Si es un cobro de títulos del Art.
          8 que ya salió al SRI, se emite su nota de crédito.
        </span>
      </p>

      <CampoDelPanel
        etiqueta="Por qué se anula"
        requerido
        ayuda="Quien lea esto dentro de seis meses no estuvo aquí. Diga qué pasó."
      >
        <textarea
          value={motivo}
          onChange={(evento) => {
            setMotivo(evento.target.value);
            setError(null);
          }}
          rows={3}
          maxLength={500}
          placeholder="Se cobró a la persona equivocada: la cédula tecleada era la de su hermano."
          className={`${claseEntradaPanel} h-auto py-2`}
        />
      </CampoDelPanel>

      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}
    </PanelLateral>
  );
}
